// Sites management types
export interface Site {
  id: number;
  projectId: number;
  siteName: string;
  description: string;
  currentState: string;
  requiredChanges: string;
  additionalWorks: string;
  creationDate: string;
  priority?: number;
  latitude?: number;
  longitude?: number;
  project?: {
    id: number;
    projectName: string;
    description?: string;
    creationDate: string;
    siteCount: string;
    discount: number;
  };
  totalPrice?: number;
}

export interface SiteCreation {
  projectId: number;
  siteName: string;
  description?: string;
  currentState?: string;
  requiredChanges?: string;
  additionalWorks?: string;
  priority: number;
  latitude: number;
  longitude: number;
}

export interface UpdateSiteRequest {
  siteName?: string;
  description?: string;
  currentState?: string;
  requiredChanges?: string;
  additionalWorks?: string;
  priority?: number;
  latitude?: number;
  longitude?: number;
}

// Keep the old interface for backward compatibility
export interface SiteChanges extends UpdateSiteRequest {
  siteName: string;
  priority: number;
  latitude: number;
  longitude: number;
}

export interface Photo {
  id?: number;
  siteId: number;
  base64Image: string; // For display purposes
}

// Normalized photo data for UI components (camelCase naming)
export interface PhotoData {
  id: number;
  dataUrl: string;
}

export interface Material {
  id: number;
  materialFormatId: number;
  materialCode: string;
  longDescription?: string;
  shortDescription?: string;
  unitType?: string;
  unitCost: number;
}

export interface SiteMaterial {
  id: number;
  siteId: number;
  materialId: number;
  materialName: string;
  quantity: number;
  unitCost: number;
  actualCost: number | null;
  totalCost: number;
  unit: string | null;
  shortDescription: string | null;
  longDescription: string | null;
  creationDate: string;
}

export interface SiteMaterialsTable {
  quantity: number;
  materialListFormatId: number;
  materialCode: string;
  longDescription?: string;
  unitType?: string;
  unitCost: number;
  totalCost: number;
}

export interface ChangedSiteMaterials {
  siteId: number;
  materialId: number;
  quantity: number;
  delete: boolean;
}

export interface NewPhotoData {
  siteId: number;
  photoData: string;
}

export interface NewPhotosData {
  siteId: number;
  photoData: string[];
}

export interface MaterialListFormat {
  id: number;
  formatName: string;
  description: string;
}

export interface ProjectMaterialListFormat {
  projectId: number;
  materialListFormatId: number;
}

// API Response types
export interface SitesApiResponse {
  data?: Site[];
  error?: {
    message: string;
    status?: number;
  };
}

export interface SiteApiResponse {
  data?: Site;
  error?: {
    message: string;
    status?: number;
  };
}

export interface MaterialsApiResponse {
  data?: Material[];
  error?: {
    message: string;
    status?: number;
  };
}

export interface SiteMaterialsApiResponse {
  data?: SiteMaterial[];
  error?: {
    message: string;
    status?: number;
  };
}

export interface PhotosApiResponse {
  data?: Photo[];
  error?: {
    message: string;
    status?: number;
  };
}

// Export types
export interface SiteExportData {
  site: Site;
  materials: SiteMaterialsTable[];
  photos: Photo[];
}

// Site management constants and types (moved from site-management)
export const getSitePriorities = (t: (key: string) => string) =>
  Array.from({ length: 5 }, (_, i) => ({
    value: String(i + 1),
    label: t(`priority_${i + 1}`),
  })) satisfies ReadonlyArray<{ value: string; label: string }>;

export const SITE_STATES = [
  { value: 'planned', label: 'site_state_planned' },
  { value: 'in_progress', label: 'site_state_in_progress' },
  { value: 'completed', label: 'site_state_completed' },
  { value: 'on_hold', label: 'site_state_on_hold' },
  { value: 'cancelled', label: 'site_state_cancelled' },
] as const;

export interface SiteCoordinates {
  latitude: number;
  longitude: number;
}

export interface SiteManagementParams {
  projectId: string;
  siteId: string;
}

export interface SiteDetails {
  id: number;
  projectId: number;
  siteName: string;
  priority: number;
  currentState: string;
  coordinates: SiteCoordinates;
  description: string;
  requiredChanges: string;
  additionalWorks?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface UpdateStatus {
  field: string;
  status: 'idle' | 'updating' | 'success' | 'error';
  error?: string;
}

export interface SiteManagementState {
  // Current site data
  currentSite: SiteDetails | null;

  // Update statuses for each field
  updateStatuses: Record<string, UpdateStatus>;

  // Loading states
  siteLoading: boolean;
  materialsLoading: boolean;
  photosLoading: boolean;

  // Error states
  siteError: string | null;
  materialsError: string | null;
  photosError: string | null;

  // Actions
  // Site actions
  loadSiteDetails: (siteId: number) => Promise<void>;
  updateSiteField: (field: keyof SiteDetails, value: unknown) => Promise<void>;

  // Utility actions
  clearErrors: () => void;
  resetUpdateStatuses: () => void;
}

// Site photo management types
export interface SitePhoto {
  id: number;
  siteId: number;
  photoUrl: string;
  thumbnailUrl?: string;
  description?: string;
  createdAt: string;
}

export interface PhotoUpload {
  siteId: number;
  photoData: string; // base64
  description?: string;
}
